import type * as React from "react";
import GLTextField, { GLTextFieldProps } from "../GLTextField";
import { validateEmailString } from "../../../../../utils/utils";

export function normalizeEmailInput(value: string | null | undefined): string {
  if (typeof value !== "string") {
    return "";
  }
  return value.replace(/\s/g, "");
}

const GLEmailField: React.FC<GLTextFieldProps> = ({
  label,
  id,
  onChange,
  tooltip,
  value,
  defaultValue,
  required,
  disabled,
  placeholder,
  additionalValidation: extraValidation,
  maxLength = 200
}) => {
  const emailFormatMessage = (normalized: string): string | undefined => {
    if (normalized.length > 0 && !validateEmailString(normalized)) {
      return "Please enter a valid email address";
    }
    return undefined;
  };

  const mergedAdditionalValidation = (
    newValue = ""
  ): string | undefined => {
    const normalized = normalizeEmailInput(newValue);
    const formatErr = emailFormatMessage(normalized);
    if (formatErr) {
      return formatErr;
    }
    return extraValidation?.(normalized);
  };

  const displayValue = value ?? "";

  const readOnlyNoHandler = !onChange;
  if (disabled || readOnlyNoHandler) {
    return (
      <GLTextField
        label={label}
        id={id}
        tooltip={tooltip}
        value={displayValue}
        defaultValue={defaultValue}
        placeholder={placeholder}
        required={required}
        disabled={true}
        onChange={onChange}
        additionalValidation={mergedAdditionalValidation}
        maxLength={maxLength}
      />
    );
  }

  const handleChange = (data: {
    e: React.ChangeEvent<HTMLInputElement>;
    isInvalid: boolean;
  }) => {
    const normalized = normalizeEmailInput(data.e.target.value);

    const formatErr = emailFormatMessage(normalized);
    const extraErr = extraValidation?.(normalized);
    const isInvalid =
      (!!required && !normalized) ||
      normalized.length > maxLength ||
      !!(formatErr ?? extraErr);

    // Do not spread `data.e.target` into a plain object — DOM nodes are not
    // plain data; `name`/`id` are often missing on the result, so
    // `handleInputChange` would set the wrong key and `formFields.email` would
    // never update (the field looks non-editable).
    onChange({
      e: {
        target: {
          name: id,
          value: normalized
        }
      } as React.ChangeEvent<HTMLInputElement>,
      isInvalid
    });
  };

  return (
    <GLTextField
      label={label}
      id={id}
      tooltip={tooltip}
      value={displayValue}
      defaultValue={defaultValue}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      onChange={handleChange}
      additionalValidation={mergedAdditionalValidation}
      maxLength={maxLength}
    />
  );
};

export default GLEmailField;
